import * as Sentry from "@sentry/react";
import { markChunkReloadInFlight } from "@/lib/chunkReloadState";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { captureUtmParamsOnLoad } from "@/lib/utmAttribution";
import { SentryErrorBoundary } from "@/components/SentryErrorBoundary";
import { resolveSentryEnvironment } from "@/lib/sentryEnvironment";
import { kinBeforeSend } from "@/lib/sentryBeforeSend";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  release: __KIN_BUILD_SHA__,
  // ⚠️ NOT import.meta.env.MODE (#869). Vercel builds preview deploys with the
  // same `vite build` as production, so MODE was "production" on both and
  // preview traffic was counted as real. The hostname is the only signal that
  // actually differs. See src/lib/sentryEnvironment.ts.
  environment: resolveSentryEnvironment(window.location.hostname),
  integrations: [
    Sentry.browserTracingIntegration(),
    // #1227 — Sentry was installed and blind to the entire silent-failure
    // class. It captures unhandled exceptions and explicit captureException
    // calls; a CAUGHT PostgREST error logged to the console produces no event.
    // That is why #1210 ran broken on every account for seven weeks with error
    // monitoring working the whole time: the query failed, the catch logged it,
    // and nothing ever reached here.
    //
    // src/ holds 182 console.error calls in files that query Supabase. This
    // line makes every one of them an event, and every one written from now on,
    // without a helper anyone has to remember to adopt.
    //
    // ⚠️ levels is ["error"] deliberately, NOT ["warn", "error"].
    // There are ~205 console.warn calls in those same files. Capturing warn
    // would (a) flood the Sentry quota with benign chatter and (b) destroy the
    // distinction this repo is standardising on — warn means "expected and
    // handled", error means "something failed and a human should see it".
    // Converting the swallowed-query-error warns to error is the follow-up;
    // widening levels here instead would look like the same fix and would
    // quietly make the signal useless.
    Sentry.captureConsoleIntegration({ levels: ["error"] }),
  ],
  tracesSampleRate: 0.1,
  // #1273 — repair the title of events synthesised from console.error. The
  // integration joins the console arguments with a space, so a PostgrestError
  // second argument stringifies to "[object Object]" — and Sentry GROUPS on
  // that title, collapsing every distinct failure from one call site into a
  // single issue. 179 of src/'s 202 console.error calls pass such an argument.
  //
  // The raw objects survive in extra.arguments, so this is repairable here in
  // one place rather than by editing 179 sites — which would fix today and not
  // the 180th site written next week. It rewrites ONLY an already-useless
  // title, and never drops an event.
  //
  // #1811 — the drop filter runs FIRST, and it is a separate function on
  // purpose. `repairConsoleEventTitle` is documented and tested as "adds
  // nothing, drops nothing"; folding a filter into it would make that comment
  // false and quietly turn a titling fix into a place events disappear. Two
  // named hooks composed here keeps each one's contract readable.
  //
  // What gets dropped is narrow: ONLY an event carrying the scope tag that
  // `reportError` sets around its own `console.error` for a connection that
  // never completed. A real failure has no such tag and is untouched.
  beforeSend: kinBeforeSend,
});

// Which commit is this phone actually running? Injected by vite.config.ts's
// `define`. Deliberately a plain global and not behind any gate — the whole
// point is that it can be read from a Safari Web Inspector console attached to
// the Median WKWebView, on production, with no build flags.
(window as unknown as Record<string, unknown>).__KIN_BUILD__ = __KIN_BUILD_SHA__;

// Capture UTM params before React mounts so OAuth redirects don't lose them.
captureUtmParamsOnLoad();

// Self-heal stale bundles: after a deploy, a session still holding old chunk
// filenames gets index.html back for a dynamic import and Vite fires
// "vite:preloadError". One reload picks up the fresh bundle. The sessionStorage
// guard caps it to one auto-reload per 60s so a genuinely offline session
// doesn't reload-loop.
const CHUNK_RELOAD_KEY = "chunk_reload_attempted";
const CHUNK_RELOAD_WINDOW_MS = 60_000;

window.addEventListener("vite:preloadError", (event) => {
  // #872 — INSTRUMENTATION ONLY. Sentry JAVASCRIPT-REACT-11 and -12 reached the
  // error boundary even though this listener exists, and we could not tell from
  // the events whether it never fired or whether the guard ate the reload. These
  // breadcrumbs record which path actually ran. They add no behavior: the three
  // statements below are in their original order and the 60s window is untouched.
  //
  // The event carries `payload: Error` and no URL field (vite/client.d.ts), so
  // the chunk URL is only available when the message happens to contain one.
  const failedUrl = event.payload?.message?.match(/https?:\/\/\S+/)?.[0];
  Sentry.addBreadcrumb({
    category: "chunk_skew",
    message: "preloadError fired",
    level: "info",
    ...(failedUrl ? { data: { url: failedUrl } } : {}),
  });

  const lastAttempt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
  const withinWindow = lastAttempt && Date.now() - lastAttempt < CHUNK_RELOAD_WINDOW_MS;

  if (withinWindow) {
    Sentry.addBreadcrumb({
      category: "chunk_skew",
      message: "reload suppressed by guard",
      level: "info",
      data: { msSinceLastReload: Date.now() - lastAttempt },
    });
    return;
  }

  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  Sentry.addBreadcrumb({
    category: "chunk_skew",
    message: "reloading",
    level: "info",
  });

  // #1251 — MUST be before preventDefault(), and must stay inside this branch.
  //
  // preventDefault() stops vite's handlePreloadError from re-throwing, which
  // makes the failed import RESOLVE with undefined instead of rejecting. The
  // lazy component then throws `_result.default` while this page keeps
  // rendering, because location.reload() schedules a navigation rather than
  // stopping execution. Marking here tells the error boundary that anything it
  // catches in that window is transient and already being fixed.
  //
  // ⚠️ Inside the branch on purpose: when the 60s guard returns early above, no
  // reload is coming, nothing is fixing it, and the boundary must show the
  // honest close-and-reopen screen instead of "Updating Kin…".
  markChunkReloadInFlight();

  event.preventDefault();
  window.location.reload();
});

// Successful boot means the current bundle is fresh — clear the guard so the
// next deploy gets its own reload attempt.
sessionStorage.removeItem(CHUNK_RELOAD_KEY);

createRoot(document.getElementById("root")!).render(
  <SentryErrorBoundary>
    <App />
  </SentryErrorBoundary>
);
